/**
 * WorkspaceStageFooter — the stage-control module rendered directly under the
 * stage rail. Combines the rule-based Next Best Action suggestions with the
 * single, consistent Advance / Retreat control pair. Both stage actions write
 * through the page's unified handlers (append/truncate stageHistory +
 * manual-override pin), so no step needs its own bespoke advance button.
 */

import React from 'react';
import type { PortalStage } from '../../types';
import type { NextBestAction } from '../../utils/nextBestActions';
import {
  PORTAL_STAGE_FULL_LABELS,
  nextPortalStage,
  prevPortalStage,
  portalStageIndex,
} from '../../utils/portalStageEngine';
import { ArrowLeft, ArrowRight, CircleAlert, Clock, Loader2, Zap } from 'lucide-react';
import './WorkspaceStageFooter.css';

const TONE_ICON = {
  action: <Zap size={13} />,
  waiting: <Clock size={13} />,
  info: <CircleAlert size={13} />,
} as const;

interface WorkspaceStageFooterProps {
  /** Raw Firestore stage — what advance/retreat operate on. */
  storedStage: PortalStage;
  selectedStep: PortalStage;
  saving: boolean;
  /** When set, the Advance button is disabled and this reason is shown. */
  advanceBlockedReason?: string | null;
  onAdvance: (target: PortalStage) => void;
  onRetreat: (target: PortalStage) => void;
  /** Rule-based next-best-action suggestions shown above the stage controls. */
  actions?: NextBestAction[];
  onAction?: (action: NextBestAction) => void;
}

const WorkspaceStageFooter: React.FC<WorkspaceStageFooterProps> = ({
  storedStage,
  selectedStep,
  saving,
  advanceBlockedReason,
  onAdvance,
  onRetreat,
  actions,
  onAction,
}) => {
  const next = nextPortalStage(storedStage);
  const prev = prevPortalStage(storedStage);
  const selectedIdx = portalStageIndex(selectedStep);
  const storedIdx = portalStageIndex(storedStage);
  // Viewing an earlier step: offer a direct retreat to it.
  const retreatTarget = selectedIdx < storedIdx ? selectedStep : prev;

  const handleRetreat = () => {
    if (!retreatTarget) return;
    const ok = window.confirm(
      `Move the workflow back to "${PORTAL_STAGE_FULL_LABELS[retreatTarget]}"? ` +
        'Later steps will be re-locked and the stage will stay put until you advance it again.',
    );
    if (ok) onRetreat(retreatTarget);
  };

  return (
    <div className="cw-stage-footer" data-tour="cw-stage-footer">
      {actions && actions.length > 0 && onAction && (
        <div className="cw-nba-strip" data-tour="cw-next-actions" role="list" aria-label="Suggested next actions">
          {actions.map(a => (
            <div key={a.id} className={`cw-nba-item tone-${a.tone}`} role="listitem" title={a.detail}>
              <span className="cw-nba-icon">{TONE_ICON[a.tone]}</span>
              <span className="cw-nba-label">{a.label}</span>
              {a.actionLabel && (
                <button type="button" className="cw-nba-btn" onClick={() => onAction(a)}>
                  {a.actionLabel}
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="cw-stage-footer-controls">
        <span className="cw-stage-footer-note">
          Workflow is at <strong>{PORTAL_STAGE_FULL_LABELS[storedStage]}</strong>
        </span>
        <div className="cw-stage-footer-actions">
          <button
            type="button"
            className="cw-stage-footer-btn retreat"
            onClick={handleRetreat}
            disabled={saving || !retreatTarget}
            title={retreatTarget ? `Retreat to ${PORTAL_STAGE_FULL_LABELS[retreatTarget]}` : 'Already at the first stage'}
          >
            {saving ? <Loader2 size={13} className="spin" /> : <ArrowLeft size={13} />}
            {retreatTarget ? `Retreat to ${PORTAL_STAGE_FULL_LABELS[retreatTarget]}` : 'Retreat'}
          </button>
          <button
            type="button"
            className="cw-stage-footer-btn advance"
            onClick={() => next && onAdvance(next)}
            disabled={saving || !next || Boolean(advanceBlockedReason)}
            title={
              advanceBlockedReason ||
              (next ? `Advance to ${PORTAL_STAGE_FULL_LABELS[next]}` : 'Already at the final stage')
            }
          >
            {next ? `Advance to ${PORTAL_STAGE_FULL_LABELS[next]}` : 'Final stage'}
            <ArrowRight size={13} />
          </button>
        </div>
      </div>
      {advanceBlockedReason && (
        <span className="cw-stage-footer-blocked">{advanceBlockedReason}</span>
      )}
    </div>
  );
};

export default WorkspaceStageFooter;
